import { useContext, useEffect, useMemo, useState } from 'react';
import { getRouteById } from '@/app/router/route-registry';
import SessionExpiryWarning from '../components/SessionExpiryWarning';
import {
  clearStoredAdminBackupSession,
  clearStoredSessionToken,
  clearStoredSessionUser,
  fetchCurrentSessionUser,
  getRuntimeEnvironment,
  getSessionTokenExpirationMs,
  getStoredSessionUser,
  isSessionExpiredError,
  isSessionRefreshRequiredError,
  getStoredSessionToken,
  loginSession,
  readJwtClaims,
  refreshSession,
  changeOwnPassword,
  updateCurrentUserEmail,
  storeActiveSession,
} from '@/app/services/session.service';
import { useTenant } from '@/tenant/tenant-provider';
import { canAccessRoute as resolveRouteAccess, getUserCapabilities } from './session-capabilities';
import { SessionContext } from './session-context';
import {
  createWorkspaceAccess,
  exitWorkspaceAccess as requestWorkspaceExit,
} from '@/modules/admin/services/admin-workspace-access.service';

const SESSION_EXPIRY_WARNING_MS = 2 * 60 * 1000;
const SESSION_EXPIRED_MESSAGE = 'Sessione scaduta. Effettua di nuovo il login.';
const SESSION_STALE_MESSAGE = "Sessione non aggiornata. Effettua di nuovo l'accesso.";

function debugControlPlaneSession(event, details = {}) {
  const runtimeEnvironment = getRuntimeEnvironment();
  if (!['development', 'dev', 'prealpha', 'test'].includes(runtimeEnvironment)) {
    return;
  }

  // Debug is intentionally limited to dev/prealpha style runtimes.
  console.info('[control-plane][session]', event, details);
}

function validateWorkspaceAccessSession({ targetTenantId, token, user }) {
  const claims = readJwtClaims(token);
  const normalizedTargetTenantId = String(targetTenantId || '').trim();
  const enabledModuleKeys = Array.isArray(claims?.enabledModuleKeys) ? claims.enabledModuleKeys : null;
  const role = typeof claims?.role === 'string' ? claims.role.trim() : '';

  if (!user || user.workspaceAccess !== true) {
    throw new Error('La sessione workspace restituita dal backend non e\' attiva.');
  }

  if (!normalizedTargetTenantId || user.tenantId !== normalizedTargetTenantId) {
    throw new Error('La sessione workspace restituita dal backend punta a un tenant diverso.');
  }

  if (!claims) {
    throw new Error('Token workspace non leggibile dal frontend.');
  }

  if (claims.workspaceAccess !== true) {
    throw new Error('Il token restituito non e\' marcato come workspace access.');
  }

  if (String(claims.tenantId || '').trim() !== normalizedTargetTenantId) {
    throw new Error('Il token workspace restituito non corrisponde al tenant richiesto.');
  }

  if (!role || (user.role && role !== user.role)) {
    throw new Error('Il ruolo della sessione workspace restituita non e\' coerente.');
  }

  if (!Array.isArray(enabledModuleKeys)) {
    throw new Error('Il token workspace non espone enabledModuleKeys.');
  }

  if (!Number.isInteger(claims.userAuthVersion) || !Number.isInteger(claims.tenantAuthVersion)) {
    throw new Error('Il token workspace non espone userAuthVersion o tenantAuthVersion.');
  }

  return {
    claims,
    enabledModuleKeys,
  };
}

export function SessionProvider({ children }) {
  const { access } = useTenant();
  const [sessionUser, setSessionUser] = useState(() => getStoredSessionUser());
  const [isLoadingSession, setIsLoadingSession] = useState(true);
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [isCheckingSession, setIsCheckingSession] = useState(false);
  const [authError, setAuthError] = useState('');
  const [sessionExpiring, setSessionExpiring] = useState(false);
  const [sessionExpiredReason, setSessionExpiredReason] = useState('');
  const [lastAuthError, setLastAuthError] = useState('');

  function clearSessionState(message = '', reason = '') {
    clearStoredAdminBackupSession();
    clearStoredSessionToken();
    clearStoredSessionUser();
    setSessionUser(null);
    setSessionExpiring(false);
    setSessionExpiredReason(reason);
    setLastAuthError(message);
    setAuthError(message);
  }

  function markSessionExpired(reason = 'expired', message = SESSION_EXPIRED_MESSAGE) {
    clearSessionState(message, reason);
  }

  useEffect(() => {
    let active = true;

    async function bootstrapSession() {
      try {
        const currentUser = await fetchCurrentSessionUser();

        if (!active) {
          return;
        }

        if (!currentUser) {
          clearSessionState('', '');
          return;
        }

        setSessionUser(currentUser);
        setSessionExpiring(false);
        setSessionExpiredReason('');
        setLastAuthError('');
        setAuthError('');
      } catch (error) {
        if (!active) {
          return;
        }

        if (isSessionRefreshRequiredError(error)) {
          clearSessionState(error.message || SESSION_STALE_MESSAGE, 'stale');
        } else if (isSessionExpiredError(error)) {
          clearSessionState(error.message || SESSION_EXPIRED_MESSAGE, 'expired');
        } else {
          clearSessionState(error.message || 'Sessione non disponibile', 'auth_error');
        }
      } finally {
        if (active) {
          setIsLoadingSession(false);
        }
      }
    }

    bootstrapSession();

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!sessionUser) {
      setSessionExpiring(false);
      return undefined;
    }

    let warningTimer = null;
    let expiryTimer = null;

    function clearTimers() {
      if (warningTimer) {
        window.clearTimeout(warningTimer);
        warningTimer = null;
      }
      if (expiryTimer) {
        window.clearTimeout(expiryTimer);
        expiryTimer = null;
      }
    }

    function evaluateTokenExpiration() {
      const expirationMs = getSessionTokenExpirationMs(getStoredSessionToken());
      if (!expirationMs) {
        return;
      }

      const remainingMs = expirationMs - Date.now();
      if (remainingMs <= 0) {
        markSessionExpired('expired', SESSION_EXPIRED_MESSAGE);
        return;
      }

      if (remainingMs <= SESSION_EXPIRY_WARNING_MS) {
        setSessionExpiring(true);
      } else {
        warningTimer = window.setTimeout(() => setSessionExpiring(true), remainingMs - SESSION_EXPIRY_WARNING_MS);
      }

      expiryTimer = window.setTimeout(
        () => markSessionExpired('expired', SESSION_EXPIRED_MESSAGE),
        remainingMs
      );
    }

    evaluateTokenExpiration();

    function handleVisibilityChange() {
      if (document.visibilityState === 'visible') {
        clearTimers();
        evaluateTokenExpiration();
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearTimers();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [sessionUser]);

  const capabilities = useMemo(
    () =>
      getUserCapabilities({
        currentUser: sessionUser,
        tenantAccess: access,
      }),
    [access, sessionUser]
  );
  const routeAccess = useMemo(
    () => ({
      canAccessRoute: (route) =>
        resolveRouteAccess({
          route,
          currentUser: sessionUser,
          tenantAccess: access,
          capabilities,
        }),
      canAccessRouteId: (routeId) =>
        resolveRouteAccess({
          route: getRouteById(routeId),
          currentUser: sessionUser,
          tenantAccess: access,
          capabilities,
        }),
    }),
    [access, capabilities, sessionUser]
  );

  async function commitInteractiveLogin(loginAction) {
    setIsAuthenticating(true);
    setAuthError('');

    try {
      const session = await loginAction();
      clearStoredAdminBackupSession();
      setSessionUser(session.user);
      setSessionExpiring(false);
      setSessionExpiredReason('');
      setLastAuthError('');
      return session.user;
    } catch (error) {
      if (isSessionRefreshRequiredError(error)) {
        clearSessionState(error.message || SESSION_STALE_MESSAGE, 'stale');
      } else if (isSessionExpiredError(error)) {
        clearSessionState(error.message || SESSION_EXPIRED_MESSAGE, 'expired');
      } else {
        clearSessionState(error.message || 'Login non riuscito', 'auth_error');
      }
      throw error;
    } finally {
      setIsAuthenticating(false);
    }
  }

  const value = useMemo(
    () => ({
      currentUser: sessionUser,
      currentTenant: sessionUser?.tenant || null,
      workspaceAccessTenant: sessionUser?.workspaceAccess ? sessionUser?.tenant || null : null,
      mustChangePassword: sessionUser?.mustChangePassword === true,
      isSuperAdmin: capabilities.isSuperAdmin,
      canAccessPlatformAdmin: capabilities.canAccessPlatformAdmin === true,
      canEnterWorkspaceAccess: capabilities.canEnterWorkspaceAccess === true,
      isWorkspaceAccess: sessionUser?.workspaceAccess === true,
      isAuthenticated: Boolean(sessionUser),
      isLoadingSession,
      isAuthenticating,
      authError,
      sessionExpiring,
      sessionExpiredReason,
      lastAuthError,
      capabilities,
      canAccessRoute: routeAccess.canAccessRoute,
      canAccessRouteId: routeAccess.canAccessRouteId,
      setCurrentUser: setSessionUser,
      login: async (credentials) =>
        commitInteractiveLogin(() => loginSession(credentials)),
      loginPlatformAdmin: async (credentials) =>
        commitInteractiveLogin(async () => {
          const session = await loginSession(credentials);

          if (session.user?.capabilities?.canAccessPlatformAdmin !== true || session.user?.workspaceAccess) {
            throw new Error('Credenziali non valide per il platform admin.');
          }

          return session;
        }),
      logout: () => {
        clearSessionState('', 'manual');
      },
      continueSession: async () => {
        setIsCheckingSession(true);
        try {
          const session = await refreshSession();
          setSessionUser(session.user);
          setSessionExpiring(false);
          setSessionExpiredReason('');
          setLastAuthError('');
          setAuthError('');
          return session.user;
        } catch (error) {
          if (isSessionRefreshRequiredError(error)) {
            clearSessionState(error.message || SESSION_STALE_MESSAGE, 'stale');
            return null;
          }
          if (isSessionExpiredError(error)) {
            clearSessionState(error.message || SESSION_EXPIRED_MESSAGE, 'expired');
            return null;
          }
          setLastAuthError(error.message || 'Sessione non disponibile');
          setAuthError(error.message || 'Sessione non disponibile');
          throw error;
        } finally {
          setIsCheckingSession(false);
        }
      },
      changePassword: async ({ currentPassword, newPassword }) => {
        setAuthError('');
        try {
          const nextUser = await changeOwnPassword({ currentPassword, newPassword });
          setSessionUser(nextUser);
          setSessionExpiring(false);
          return nextUser;
        } catch (error) {
          if (isSessionRefreshRequiredError(error) || isSessionExpiredError(error)) {
            clearSessionState(
              error.message || (isSessionRefreshRequiredError(error) ? SESSION_STALE_MESSAGE : SESSION_EXPIRED_MESSAGE),
              isSessionRefreshRequiredError(error) ? 'stale' : 'expired'
            );
          }
          throw error;
        }
      },
      updateCurrentUserEmail: async (email) => {
        setAuthError('');
        try {
          const session = await updateCurrentUserEmail(email);
          clearStoredAdminBackupSession();
          setSessionUser(session.user);
          setSessionExpiring(false);
          setSessionExpiredReason('');
          setLastAuthError('');
          return session.user;
        } catch (error) {
          if (isSessionRefreshRequiredError(error) || isSessionExpiredError(error)) {
            clearSessionState(
              error.message || (isSessionRefreshRequiredError(error) ? SESSION_STALE_MESSAGE : SESSION_EXPIRED_MESSAGE),
              isSessionRefreshRequiredError(error) ? 'stale' : 'expired'
            );
          }
          throw error;
        }
      },
      refreshCurrentUser: async () => {
        setAuthError('');
        try {
          const nextUser = await fetchCurrentSessionUser();
          if (!nextUser) {
            clearSessionState('Sessione non disponibile. Effettua di nuovo il login.', 'auth_error');
            return null;
          }
          setSessionUser(nextUser);
          setSessionExpiring(false);
          return nextUser;
        } catch (error) {
          if (isSessionRefreshRequiredError(error) || isSessionExpiredError(error)) {
            clearSessionState(
              error.message || (isSessionRefreshRequiredError(error) ? SESSION_STALE_MESSAGE : SESSION_EXPIRED_MESSAGE),
              isSessionRefreshRequiredError(error) ? 'stale' : 'expired'
            );
          }
          throw error;
        }
      },
      enterWorkspaceAccess: async (tenantId) => {
        if (!sessionUser || capabilities.canEnterWorkspaceAccess !== true || sessionUser.workspaceAccess) {
          throw new Error('Workspace access non disponibile per la sessione corrente');
        }

        const activeToken = getStoredSessionToken();
        if (!activeToken) {
          throw new Error('Sessione admin non disponibile');
        }

        setAuthError('');
        clearStoredAdminBackupSession();

        const result = await createWorkspaceAccess(tenantId);
        if (!result.ok) {
          if (
            result.error === 'WORKSPACE_ACCESS_SESSION_REFRESH_REQUIRED' ||
            result.error === 'WORKSPACE_ACCESS_SESSION_EXPIRED'
          ) {
            clearSessionState(
              result.message || 'Sessione admin non valida. Effettua di nuovo il login.',
              result.error === 'WORKSPACE_ACCESS_SESSION_REFRESH_REQUIRED' ? 'stale' : 'expired'
            );
          }
          throw new Error(result.message || 'Accesso workspace non riuscito');
        }

        try {
          const nextUser = await fetchCurrentSessionUser(result.data.token);
          const verification = validateWorkspaceAccessSession({
            targetTenantId: tenantId,
            token: result.data.token,
            user: nextUser,
          });
          debugControlPlaneSession('workspace-access-committed', {
            tenantId,
            role: nextUser?.role || '',
            enabledModuleKeys: verification.enabledModuleKeys,
            userAuthVersion: verification.claims?.userAuthVersion,
            tenantAuthVersion: verification.claims?.tenantAuthVersion,
          });
          storeActiveSession({
            token: result.data.token,
            user: nextUser,
          });
          setSessionUser(nextUser);
          setSessionExpiring(false);
          return nextUser;
        } catch (error) {
          debugControlPlaneSession('workspace-access-rollback', {
            tenantId,
            reason: error?.message || 'workspace_access_verification_failed',
          });
          storeActiveSession({
            token: activeToken,
            user: sessionUser,
          });
          clearStoredAdminBackupSession();
          setSessionUser(sessionUser);
          throw error;
        }
      },
      exitWorkspaceAccess: async () => {
        if (!sessionUser?.workspaceAccess) {
          throw new Error('Workspace access non attivo per la sessione corrente');
        }

        const result = await requestWorkspaceExit();
        if (!result.ok) {
          if (
            result.error === 'WORKSPACE_ACCESS_SESSION_REFRESH_REQUIRED' ||
            result.error === 'WORKSPACE_ACCESS_SESSION_EXPIRED'
          ) {
            clearSessionState(
              result.message || 'Sessione workspace non valida. Effettua di nuovo il login.',
              result.error === 'WORKSPACE_ACCESS_SESSION_REFRESH_REQUIRED' ? 'stale' : 'expired'
            );
            return null;
          }
          throw new Error(result.message || 'Chiusura workspace access non riuscita');
        }

        const restoredUser = result.data?.user || null;
        const restoredToken = result.data?.token || '';
        if (!restoredUser || !restoredToken) {
          clearStoredSessionToken();
          clearStoredSessionUser();
          clearStoredAdminBackupSession();
          setSessionUser(null);
          clearSessionState('Sessione platform non ripristinata. Effettua di nuovo il login.', 'auth_error');
          return null;
        }

        storeActiveSession({
          token: restoredToken,
          user: restoredUser,
        });
        clearStoredAdminBackupSession();
        setSessionUser(restoredUser);
        setSessionExpiring(false);
        setSessionExpiredReason('');
        setLastAuthError('');
        setAuthError('');
        return restoredUser;
      },
    }),
    [
      authError,
      capabilities,
      isAuthenticating,
      isCheckingSession,
      isLoadingSession,
      lastAuthError,
      routeAccess,
      sessionExpiredReason,
      sessionExpiring,
      sessionUser,
    ]
  );

  return (
    <SessionContext.Provider value={value}>
      {children}
      <SessionExpiryWarning
        open={sessionExpiring && Boolean(sessionUser)}
        isChecking={isCheckingSession}
        onContinue={value.continueSession}
        onLogin={() => markSessionExpired('expired', SESSION_EXPIRED_MESSAGE)}
      />
    </SessionContext.Provider>
  );
}

export function useSession() {
  const context = useContext(SessionContext);

  if (!context) {
    throw new Error('useSession must be used within a SessionProvider');
  }

  return context;
}

export { useOptionalSession } from './session-context';
